import { useState } from "react";
import GeneralButton from "../common/GeneralButton";
import { AnimatePresence, motion } from "framer-motion";
import { usePromoCode } from "../contexts/SubscriptionContext"; // ✅ make sure this context includes applyCoupon & removeCoupon
import { useParams } from "react-router-dom";

const PromoCodeSection = ({
  cartId,
  mealSubscriptionCartId,
  appliedCoupon,
}) => {
  const [promo, setPromo] = useState({
    value: localStorage.getItem("promoCodeSaved") || "",
    applied: appliedCoupon || null,
    error: "",
    loading: false,
  });

  const { type } = useParams(); // you can use this if you need to detect type
  const { applyCoupon, removeCoupon } = usePromoCode();

  // ✅ Handle Apply
  const handleApply = async () => {
    if (!promo.value.trim()) {
      setPromo((prev) => ({ ...prev, error: "Please enter a promo code" }));
      return;
    }

    setPromo((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const result = await applyCoupon({
        cartId: type === "order" ? cartId : "",
        mealSubscriptionCartId: type === "meals" ? mealSubscriptionCartId : "",
        couponCode: promo.value.trim(),
      });

      if (result.success) {
        localStorage.setItem("promoCodeSaved", promo.value.trim());
        setPromo((prev) => ({
          ...prev,
          applied: promo.value.trim(),
          loading: false,
        }));
      } else {
        setPromo((prev) => ({
          ...prev,
          error: result.error || "Invalid coupon code",
          loading: false,
        }));
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setPromo((prev) => ({
        ...prev,
        error: "Something went wrong",
        loading: false,
      }));
    }
  };

  // ✅ Handle Remove
  const handleRemove = async () => {
    setPromo((prev) => ({ ...prev, loading: true }));

    try {
      const result = await removeCoupon({
        cartId: type === "order" ? cartId : "",
        mealSubscriptionCartId: type === "meals" ? mealSubscriptionCartId : "",
      });
      if (result.success) {
        setPromo({ value: "", applied: null, error: "", loading: false });
      }
    } catch (err) {
      console.error(err);
      setPromo((prev) => ({
        ...prev,
        error: "Failed to remove coupon",
        loading: false,
      }));
    }
  };

  return (
    <div className="relative min-h-[76px]">
      <AnimatePresence mode="wait">
        {!promo.applied ? (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 mt-4 absolute inset-0"
          >
            <div className="relative font-mono px-4 text-18pt h-[56px] w-full border border-green rounded-[15px] flex items-center justify-between">
              <input
                type="text"
                placeholder="Promo code"
                value={promo.value}
                onChange={(e) =>
                  setPromo({ ...promo, value: e.target.value, error: "" })
                }
                className="w-full h-full bg-white focus:outline-none text-quaternary text-18pt promo"
                disabled={promo.loading}
              />

              <GeneralButton
                title="Click to apply code"
                text={promo.loading ? "Applying..." : "Apply"}
                textColor="#fff"
                className="text-18pt ml-4"
                bgColor="#30160E"
                onClick={handleApply}
                icon={null}
                disabled={promo.loading}
              />
            </div>
            {promo.error && (
              <div className="relative">
                <motion.p
                  className="errorMessage2 mt-1  w-full text-wrap"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {promo.error}
                </motion.p>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="applied"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 mt-4 absolute inset-0"
          >
            <div className="relative font-mono px-4 text-18pt h-[56px] w-full border border-green rounded-[15px] flex items-center justify-between">
              <p className="text-quaternary text-18pt font-sans">
                {promo.applied}
              </p>

              <GeneralButton
                text="Remove"
                title="Click to remove code"
                textColor="#fff"
                icon={null}
                className="text-18pt ml-4"
                bgColor="#D06F36"
                onClick={handleRemove}
                disabled={promo.loading}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PromoCodeSection;
